import { useEffect, useState } from "react";
import { useFormikContext } from "formik";
import { Label } from "../Text";
import { Button } from "../Buttons";
import { requestAuth } from "../services/RequestService";

/**
 * Constructor visual de campos para "Lista dinámica manual".
 * Guarda en Formik (Format) un JSON:
 * {
 *   "fields": [
 *     { "label": "Cargo", "type": "text" },
 *     { "label": "Estado", "type": "select", "dropdownUrl": "estadosDeFactura", "options": [...] }
 *   ]
 * }
 *
 * Si type === "select":
 *  - Puede tomar opciones de Gestión de listas desplegables (dropdownUrl)
 *  - O definir opciones manuales
 */

const FIELD_TYPES = [
  { value: "text", label: "Texto" },
  { value: "textarea", label: "Texto multilínea" },
  { value: "select", label: "Lista desplegable" },
  { value: "date", label: "Fecha" },
  { value: "number", label: "Número" },
  { value: "file", label: "Archivo" },
];

const DEFAULT_FIELDS = [
  { label: "Cargo", type: "text", options: [], dropdownUrl: "", optionsSource: "manual" },
  { label: "Descripcion", type: "textarea", options: [], dropdownUrl: "", optionsSource: "manual" },
  {
    label: "Estado",
    type: "select",
    optionsSource: "sistema",
    dropdownUrl: "",
    options: [
      { value: "Valida", label: "Valida" },
      { value: "Rechazada", label: "Rechazada" },
      { value: "Anulada", label: "Anulada" },
    ],
  },
];

function parseInitial(raw) {
  if (!raw)
    return DEFAULT_FIELDS.map((f) => ({
      ...f,
      options: f.options ? [...f.options] : [],
    }));
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    const list = Array.isArray(parsed?.fields)
      ? parsed.fields
      : Array.isArray(parsed)
      ? parsed
      : null;
    if (!list?.length)
      return DEFAULT_FIELDS.map((f) => ({ ...f, options: [...(f.options || [])] }));
    return list.map((f) => ({
      label: f.label || "",
      type: (f.type || "text").toLowerCase(),
      dropdownUrl: f.dropdownUrl || f.url || "",
      optionsSource: f.dropdownUrl || f.url ? "sistema" : "manual",
      options: Array.isArray(f.options)
        ? f.options.map((o) =>
            typeof o === "string"
              ? { value: o, label: o }
              : { value: o.value ?? o.name ?? "", label: o.label ?? o.name ?? o.value ?? "" }
          )
        : [],
    }));
  } catch {
    return String(raw)
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean)
      .map((label) => ({
        label,
        type: "text",
        options: [],
        dropdownUrl: "",
        optionsSource: "manual",
      }));
  }
}

const InputFieldSchemaBuilder = ({ field }) => {
  const { setFieldValue, values } = useFormikContext();
  const name = field.name;

  const [rows, setRows] = useState(() => parseInitial(values?.[name]));
  const [systemLists, setSystemLists] = useState([]);
  const [loadingLists, setLoadingLists] = useState(false);

  // Cargar listas de Gestión de listas desplegables
  useEffect(() => {
    let cancelled = false;
    setLoadingLists(true);
    requestAuth("get", "/Dropdown/List")
      .then((res) => {
        if (cancelled) return;
        const data = res?.data ?? res ?? [];
        const arr = Array.isArray(data) ? data : data?.data ?? [];
        setSystemLists(arr);
      })
      .catch(() => {
        if (!cancelled) setSystemLists([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingLists(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Sincronizar JSON hacia Formik
  useEffect(() => {
    const payload = {
      fields: rows.map((r) => {
        const item = { label: r.label || "Campo", type: r.type || "text" };
        if (r.type === "select") {
          if (r.optionsSource === "sistema" && r.dropdownUrl) {
            item.dropdownUrl = r.dropdownUrl;
          }
          item.options = (r.options || [])
            .filter((o) => o.value || o.label)
            .map((o) => ({
              value: o.value || o.label,
              label: o.label || o.value,
            }));
        }
        return item;
      }),
    };
    setFieldValue(name, JSON.stringify(payload));
  }, [rows, name, setFieldValue]);

  function updateRow(index, patch) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function addRow() {
    if (rows.length >= 4) return;
    setRows((prev) => [
      ...prev,
      {
        label: "",
        type: "text",
        options: [],
        dropdownUrl: "",
        optionsSource: "manual",
      },
    ]);
  }

  function removeRow(index) {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  function addOption(rowIndex) {
    setRows((prev) =>
      prev.map((r, i) =>
        i === rowIndex
          ? { ...r, options: [...(r.options || []), { value: "", label: "" }] }
          : r
      )
    );
  }

  function updateOption(rowIndex, optIndex, patch) {
    setRows((prev) =>
      prev.map((r, i) => {
        if (i !== rowIndex) return r;
        const options = (r.options || []).map((o, j) =>
          j === optIndex ? { ...o, ...patch } : o
        );
        return { ...r, options };
      })
    );
  }

  function removeOption(rowIndex, optIndex) {
    setRows((prev) =>
      prev.map((r, i) =>
        i === rowIndex
          ? { ...r, options: (r.options || []).filter((_, j) => j !== optIndex) }
          : r
      )
    );
  }

  /** Al elegir una lista del sistema, cargar sus opciones */
  async function handleSelectSystemList(rowIndex, urlLabel) {
    updateRow(rowIndex, { dropdownUrl: urlLabel, optionsSource: "sistema" });
    if (!urlLabel) {
      updateRow(rowIndex, { options: [] });
      return;
    }
    try {
      const res = await requestAuth("get", `/Dropdown/${urlLabel}/options`);
      const data = res?.data ?? res ?? [];
      const arr = Array.isArray(data) ? data : data?.data ?? [];
      const options = arr.map((o) => ({
        value: o.name ?? o.value ?? o.label ?? String(o),
        label: o.name ?? o.label ?? o.value ?? String(o),
      }));
      updateRow(rowIndex, { options, dropdownUrl: urlLabel, optionsSource: "sistema" });
    } catch {
      updateRow(rowIndex, { options: [], dropdownUrl: urlLabel, optionsSource: "sistema" });
    }
  }

  function listLabel(item) {
    return item?.name || item?.label || item?.urlLabel || item?.url || "Lista";
  }

  function listValue(item) {
    return item?.urlLabel || item?.url || item?.name || "";
  }

  return (
    <div className="w-full flex flex-col gap-3">
      <Label className="my-0">{field.label || "Nombre de campos"}</Label>
      <p className="text-xs text-gray-500 -mt-1">
        Define hasta 4 campos (texto, lista, fecha, número o archivo). En &quot;Lista desplegable&quot; puedes usar una lista de{" "}
        <strong>Gestión de listas desplegables</strong> o escribir opciones a mano.
      </p>

      {rows.map((row, index) => (
        <div
          key={index}
          className="bg-white border border-gray-300 rounded-lg p-3 flex flex-col gap-2"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-blue-900">
              Campo {index + 1}
            </span>
            {rows.length > 1 && (
              <button
                type="button"
                onClick={() => removeRow(index)}
                className="text-red-500 text-sm hover:underline"
              >
                <i className="fas fa-trash mr-1" />
                Quitar
              </button>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="mt-0 text-sm">Etiqueta</Label>
              <input
                type="text"
                className="w-full p-2 rounded-md border border-gray-300"
                value={row.label}
                placeholder="Ej: Cargo"
                onChange={(e) => updateRow(index, { label: e.target.value })}
              />
            </div>
            <div>
              <Label className="mt-0 text-sm">Tipo</Label>
              <select
                className="w-full p-2 rounded-md border border-gray-300"
                value={row.type}
                onChange={(e) => {
                  const type = e.target.value;
                  updateRow(index, {
                    type,
                    optionsSource: type === "select" ? "sistema" : "manual",
                    dropdownUrl: type === "select" ? row.dropdownUrl : "",
                    options: type === "select" ? row.options || [] : [],
                  });
                }}
              >
                {FIELD_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {row.type === "select" && (
            <div className="bg-gray-50 rounded-md p-2 flex flex-col gap-2">
              {/* Origen: sistema o manual */}
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name={`src-${index}`}
                    checked={row.optionsSource !== "manual"}
                    onChange={() =>
                      updateRow(index, { optionsSource: "sistema" })
                    }
                  />
                  Lista del sistema
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name={`src-${index}`}
                    checked={row.optionsSource === "manual"}
                    onChange={() =>
                      updateRow(index, {
                        optionsSource: "manual",
                        dropdownUrl: "",
                      })
                    }
                  />
                  Opciones manuales
                </label>
              </div>

              {row.optionsSource !== "manual" ? (
                <>
                  <Label className="mt-0 text-sm">
                    Lista de Gestión de listas desplegables
                  </Label>
                  <select
                    className="w-full p-2 rounded-md border border-gray-300"
                    value={row.dropdownUrl || ""}
                    disabled={loadingLists}
                    onChange={(e) =>
                      handleSelectSystemList(index, e.target.value)
                    }
                  >
                    <option value="">
                      {loadingLists
                        ? "Cargando listas..."
                        : "Seleccione una lista..."}
                    </option>
                    {systemLists.map((item, i) => (
                      <option key={i} value={listValue(item)}>
                        {listLabel(item)}
                      </option>
                    ))}
                  </select>
                  {row.dropdownUrl && (row.options || []).length > 0 && (
                    <p className="text-xs text-gray-600">
                      Opciones cargadas:{" "}
                      {(row.options || []).map((o) => o.label).join(", ")}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <Label className="mt-0 text-sm">Opciones de la lista</Label>
                  {(row.options || []).map((opt, oi) => (
                    <div key={oi} className="flex gap-2 items-center">
                      <input
                        type="text"
                        className="flex-1 p-1.5 rounded-md border border-gray-300 text-sm"
                        placeholder="Valor"
                        value={opt.value}
                        onChange={(e) => {
                          const v = e.target.value;
                          updateOption(index, oi, {
                            value: v,
                            label:
                              opt.label === opt.value || !opt.label
                                ? v
                                : opt.label,
                          });
                        }}
                      />
                      <input
                        type="text"
                        className="flex-1 p-1.5 rounded-md border border-gray-300 text-sm"
                        placeholder="Etiqueta visible"
                        value={opt.label}
                        onChange={(e) =>
                          updateOption(index, oi, { label: e.target.value })
                        }
                      />
                      <button
                        type="button"
                        className="text-red-500 text-sm px-1"
                        onClick={() => removeOption(index, oi)}
                      >
                        <i className="fas fa-times" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addOption(index)}
                    className="text-blue-700 text-sm self-start hover:underline"
                  >
                    + Agregar opción
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      ))}

      {rows.length < 4 && (
        <Button
          type="button"
          onClick={addRow}
          className="px-3 py-1 self-start bg-blue-600 text-white"
        >
          + Agregar campo
        </Button>
      )}
      {rows.length >= 4 && (
        <p className="text-xs text-amber-600">
          Máximo 4 campos (límite del sistema de subdatos).
        </p>
      )}
    </div>
  );
};

export default InputFieldSchemaBuilder;